import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Product } from "@/types";
import { fetchProducts } from "@/lib/api";
import { useI18n } from "@/i18n";

interface CatalogContextValue {
  products: Product[];
  loading: boolean;
  getProduct: (id: string) => Product | undefined;
  refresh: () => void;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

/**
 * Fetches the full catalog once on boot and keeps it in memory. Catalog/
 * Product/Home pages still call the API's own filtered endpoints directly
 * (server-side search/category filtering, always fresh) — this cache exists
 * so the cart and favorites screens can synchronously resolve a productId
 * they're holding onto without a network round trip per line item, and so
 * a stale cart survives even if a product was since removed from the
 * customer-facing filtered views (still shows its last-known name/price).
 */
export function CatalogProvider({ children }: { children: React.ReactNode }) {
  const { locale } = useI18n();
  // Instant start: show the last catalogue from this device, then refresh it.
  const cacheKey = `kms.catalog.${locale}`;
  const [products, setProducts] = useState<Product[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(cacheKey) ?? "[]") as Product[];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchProducts()
      .then((list) => {
        setProducts(list);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(list));
        } catch {
          /* storage full or unavailable — fine, it's only a cache */
        }
      })
      .catch(() => {
        /* network hiccup: keep whatever we had; pages show their own errors */
      })
      .finally(() => setLoading(false));
  }, [cacheKey]);

  // Reload when the language changes so names/descriptions follow it.
  useEffect(() => refresh(), [refresh, locale]);

  const getProduct = useCallback((id: string) => products.find((p) => p.id === id), [products]);

  const value = useMemo(() => ({ products, loading, getProduct, refresh }), [products, loading, getProduct, refresh]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog must be used within CatalogProvider");
  return ctx;
}
